import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDepth } from "../lib/queries.js";
import { venueColorMap } from "../lib/venueColors.js";
import { DepthPanel, PartnerLadder } from "../components/DepthPanel.js";

/**
 * Full-page depth: the shared consolidated DepthPanel (venue dots + per-level
 * hover breakdown) plus the per-venue sub-ladders that don't fit inline.
 */
export function DepthView() {
  const { hash } = useParams<{ hash: string }>();
  const positionHash = hash ? decodeURIComponent(hash) : undefined;
  const navigate = useNavigate();
  const depthQ = useDepth(positionHash);
  const partners = depthQ.data?.partners ?? [];
  const colors = useMemo(
    () => venueColorMap(partners.map((p) => p.partner_name ?? p.partner_id)),
    [partners],
  );

  if (!positionHash) return null;

  return (
    <div className="flex h-full flex-col overflow-auto">
      <div className="flex items-center gap-3 border-b border-border bg-panel-2 px-3 py-2">
        <button onClick={() => navigate(-1)} className="text-[11px] text-dim hover:text-amber">
          ← back
        </button>
        <h1 className="min-w-0 break-words text-xs text-ink" title={positionHash}>
          depth · <span className="text-dim">{positionHash}</span>
        </h1>
      </div>

      <div className="pt-3">
        <DepthPanel positionHash={positionHash} />
      </div>

      {partners.length > 0 && (
        <div className="px-2 pb-4">
          <div className="px-1 pb-1.5 text-[10px] uppercase tracking-wide text-faint">
            per venue
          </div>
          <div className="flex flex-wrap gap-3">
            {partners.map((p) => (
              <PartnerLadder
                key={p.partner_id}
                partner={p}
                color={colors.get(p.partner_name ?? p.partner_id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
